import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import {
  Container,
  Box,
  Grid,
  FormControl,
  Button,
  TextField,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  Paper,
  Alert,
  Snackbar,
  Checkbox,
  Divider,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Collapse,
  Tooltip,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputAdornment,
  Switch,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import {
  addUser,
  updateUser,
  changeUserEmail,
  getUserAudit,
  releaseUserEmail,
  disableUser as disableUserService,
} from "../../services/backend/UserService";
import { addUser as addUserToStore, updateUsers } from "../../redux/slices/userSlice";
import { CONTRACT_TYPES, dependencies, PERMISSIONS, ROLES } from "../../constants";
import {
  getContractConfig,
  DEFAULT_CONTRACT_CONFIG,
  allNatureOptions,
  allNatureEntries,
  isInactiveValue,
} from "../../services/backend/ContractConfigService";
import { debugLog } from "../../functions";
import { ALL_ROLES, ROLE_LABELS } from "./roleLabels";

// Naturalezas del catálogo configurable (config/contracts) para el selector de
// allowedNatures del rol FINANCIER; las semillas de constants.js aplican
// mientras carga o si el documento no existe.
function useNatureCatalog() {
  const [cfg, setCfg] = useState(DEFAULT_CONTRACT_CONFIG);
  useEffect(() => {
    (async () => setCfg(await getContractConfig()))();
  }, []);
  return cfg;
}

// Un valor asignado que quedó desactivado (o renombrado) en el catálogo se
// conserva visible con distintivo para poder mantenerlo o retirarlo.
const natureLabelFor = (cfg) => (option) =>
  isInactiveValue(allNatureEntries(cfg), option) ? `${option} (inactivo)` : option;

const USER_TEMPLATES = {
  EXPEDIENTS: {
    label: "Perfil de Expedientes",
    // The EXPEDIENTS role carries expedient.view + expedient.list, the two
    // permissions this template used to add by hand.
    roles: ["USER", "EXPEDIENTS"],
    permissions: [],
  },
  PROCESSES: {
    label: "Perfil de Usuario (Procesos)",
    roles: ["USER"],
    permissions: [],
  },
  CONTRACTS: {
    label: "Perfil de Contratos",
    roles: ["CONTRACTOR"],
    permissions: [],
  },
};

const ADMIN_ONLY_ROLES = ["ADMIN", "ADMIN_USER"];

// MY_ENTITY is out of the permission tuner for the same reason company roles are
// out of ALL_ROLES (see roleLabels.js): it describes
// what a company does to its own record, and there is no sense granting it to an
// employee, who has no entity of their own to point at.
const PERMISSION_DOMAINS = Object.keys(PERMISSIONS)
  .filter((domain) => domain !== "MY_ENTITY")
  .map((domain) => ({
    domain,
    permissions: Object.entries(PERMISSIONS[domain]).map(([action, value]) => ({
      action,
      value,
    })),
  }));

// Al quitar un rol solo se restan los permisos que NINGÚN otro rol vigente
// aporta. Restar por cadena dejaba al usuario con el rol puesto y sin permisos
// cuando dos roles comparten cadenas: SUPERVISOR_LEGAL_CONTRACT es espejo de
// SUPERVISOR_CONTRACT, y PQRSD_DEPENDENCY_SUPERVISOR un superconjunto de
// PQRSD_OPERATOR. Los permisos afinados a mano que no pertenecen al rol
// removido tampoco se tocan.
const permissionsAfterRoleRemoval = (permissions, removedRole, remainingRoles) => {
  const removed = new Set(ROLES[removedRole] || []);
  const kept = new Set(remainingRoles.flatMap((r) => ROLES[r] || []));
  return permissions.filter((p) => !removed.has(p) || kept.has(p));
};

const ADD_USER_ERRORS = {
  EMAIL_IN_USE: "El correo electrónico ya está registrado",
  EMAIL_HELD_BY_DISABLED:
    "El correo pertenece a un usuario deshabilitado. Libere su correo en Deshabilitar Usuarios antes de reutilizarlo.",
  INVALID_EMAIL: "El correo electrónico no es válido",
  INVALID_PASSWORD: "La contraseña debe tener al menos 6 caracteres",
  FORBIDDEN: "No tiene permiso para crear este usuario",
};

const getErrorMessage = (error) => ADD_USER_ERRORS[error] || "Error al crear el usuario";

// A user's document id, copyable: support asks for it when tracing a record.
function CopyableUserId({ userId }) {
  const [copied, setCopied] = useState(false);
  if (!userId) return null;
  const copy = () => {
    navigator.clipboard.writeText(userId);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {userId}
      </Typography>
      <Tooltip title={copied ? "¡Copiado!" : "Copiar ID"}>
        <IconButton size="small" onClick={copy} aria-label="Copiar ID del usuario">
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function RolePermissionsTable({ selectedRoles, onToggleRole, extraPermissions = [], onTogglePermission, isFullAdmin = true }) {
  const [expandedDomain, setExpandedDomain] = useState(null);

  const availableRoles = useMemo(() => {
    if (isFullAdmin) return ALL_ROLES;
    return ALL_ROLES.filter((r) => !ADMIN_ONLY_ROLES.includes(r.key));
  }, [isFullAdmin]);

  const resolvedPermissions = useMemo(() => new Set(extraPermissions), [extraPermissions]);

  const toggleDomain = (domain) => {
    setExpandedDomain((prev) => (prev === domain ? null : domain));
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
        Roles del Usuario
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.50" }}>
              <TableCell padding="checkbox" />
              <TableCell sx={{ fontWeight: 600 }}>Rol</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Permisos incluidos</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {availableRoles.map(({ key, label }) => {
              const rolePerms = ROLES[key] || [];
              const isSelected = selectedRoles.includes(key);
              return (
                <TableRow
                  key={key}
                  hover
                  onClick={() => onToggleRole(key)}
                  sx={{ cursor: "pointer", bgcolor: isSelected ? "primary.50" : "inherit" }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox checked={isSelected} color="primary" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 400 }}>
                      {label}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {rolePerms.length} permiso{rolePerms.length !== 1 ? "s" : ""}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Permisos resultantes ({resolvedPermissions.size})
        {extraPermissions.length > 0 && (
          <Chip
            label={`+${extraPermissions.length} de plantilla`}
            size="small"
            color="info"
            variant="outlined"
            sx={{ ml: 1 }}
          />
        )}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.50" }}>
              <TableCell sx={{ fontWeight: 600, width: 40 }} />
              <TableCell sx={{ fontWeight: 600 }}>Módulo</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Permisos activos</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {PERMISSION_DOMAINS.map(({ domain, permissions }) => {
              const activePerms = permissions.filter((p) => resolvedPermissions.has(p.value));
              const isExpanded = expandedDomain === domain;
              return (
                <React.Fragment key={domain}>
                  <TableRow
                    hover
                    onClick={() => toggleDomain(domain)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="none" sx={{ pl: 1 }}>
                      <IconButton size="small">
                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {domain}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${activePerms.length} / ${permissions.length}`}
                        size="small"
                        color={activePerms.length === permissions.length ? "success" : "default"}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={3} sx={{ p: 0, borderBottom: isExpanded ? undefined : "none" }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ pl: 6, pr: 2, py: 1, bgcolor: "grey.50" }}>
                          {permissions.map((p) => {
                            const isActive = resolvedPermissions.has(p.value);
                            return (
                              <Chip
                                key={p.value}
                                label={p.action}
                                size="small"
                                sx={{ m: 0.3, cursor: "pointer" }}
                                color={isActive ? "primary" : "default"}
                                variant={isActive ? "filled" : "outlined"}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (onTogglePermission) {
                                    onTogglePermission(p.value);
                                  }
                                }}
                              />
                            );
                          })}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function CreatedUserCard({ createdUser, onDismiss }) {
  const [copied, setCopied] = useState(null);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 1500);
  };

  if (!createdUser) return null;

  return (
    <Card
      elevation={0}
      sx={{
        mb: 3,
        border: "1px solid",
        borderColor: "success.light",
        bgcolor: "success.50",
      }}
    >
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
          <CheckCircleOutlineIcon color="success" />
          <Typography variant="h6" color="success.dark">
            Usuario Creado Exitosamente
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">ID del Usuario</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                {createdUser.userId}
              </Typography>
              <Tooltip title={copied === "id" ? "¡Copiado!" : "Copiar ID"}>
                <IconButton size="small" onClick={() => copyToClipboard(createdUser.userId, "id")}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Correo Electrónico</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="body2">{createdUser.email}</Typography>
              <Tooltip title={copied === "email" ? "¡Copiado!" : "Copiar correo"}>
                <IconButton size="small" onClick={() => copyToClipboard(createdUser.email, "email")}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary">Nombre Completo</Typography>
            <Typography variant="body2">
              {createdUser.usertitle} {createdUser.name} {createdUser.lastName}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary">NUIP</Typography>
            <Typography variant="body2">{createdUser.nuip}</Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary">Cargo</Typography>
            <Typography variant="body2">{createdUser.position}</Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">Roles Asignados</Typography>
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
              {createdUser.roles?.length > 0 ? (
                createdUser.roles.map((role) => (
                  <Chip key={role} label={ROLE_LABELS[role] || role} size="small" color="primary" variant="outlined" />
                ))
              ) : (
                <Chip label="Sin roles asignados" size="small" color="warning" variant="outlined" />
              )}
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button size="small" onClick={onDismiss}>
            Cerrar
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

// Fields of the "Perfil" section, in the same order as the create form.
const PROFILE_FIELDS = ["usertitle", "name", "lastName", "nuip", "city", "entity", "dependence", "level", "position"];
// A company account has no institutional placement: UserProfile.js hides these
// for them too.
const STAFF_ONLY_FIELDS = ["entity", "dependence", "level"];

const profileOf = (u) =>
  Object.fromEntries(PROFILE_FIELDS.map((f) => [f, (u && u[f]) || ""]));

const EMAIL_CHANGE_ERRORS = {
  EMAIL_IN_USE: "Ese correo ya pertenece a otra cuenta.",
  EMAIL_HELD_BY_DISABLED:
    "Ese correo pertenece a un usuario deshabilitado. Libere su correo en Deshabilitar Usuarios primero.",
  SAME_EMAIL: "El usuario ya tiene ese correo.",
  INVALID_EMAIL: "El correo no es válido.",
  ENTITY_ACCOUNT: "Las cuentas de empresa cambian su correo desde su propio perfil.",
  FORBIDDEN: "Solo un administrador puede cambiar el correo de otro usuario.",
};

function ChangeEmailDialog({ open, targetUser, currentUser, onClose, onChanged }) {
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setNewEmail("");
      setError("");
    }
  }, [open]);

  const handleConfirm = async () => {
    setSaving(true);
    setError("");
    const response = await changeUserEmail(targetUser.userId, newEmail, currentUser);
    setSaving(false);
    if (response && response.result) {
      onChanged(response.email);
    } else {
      setError(EMAIL_CHANGE_ERRORS[response && response.error] || "No fue posible cambiar el correo.");
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Cambiar correo</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Correo actual: <strong>{targetUser && targetUser.email}</strong>
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          type="email"
          label="Nuevo correo"
          value={newEmail}
          onChange={(evt) => setNewEmail(evt.target.value)}
          disabled={saving}
        />
        <Alert severity="warning" sx={{ mt: 2 }}>
          El usuario deberá iniciar sesión con el nuevo correo; su contraseña no cambia. Sus
          sesiones abiertas se cerrarán.
        </Alert>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={saving || !newEmail.trim()}>
          {saving ? <CircularProgress size={20} /> : "Cambiar correo"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const AUDIT_ACTION_LABELS = {
  CREATE_USER: "Creación",
  UPDATE_USER: "Actualización",
  DISABLE_USER: "Deshabilitado",
  ENABLE_USER: "Habilitado",
  CHANGE_EMAIL: "Cambio de correo",
  RELEASE_EMAIL: "Correo liberado",
  // Written by the entities package to entityAudit.
  SET_MEMBER_ROLE: "Cambio de rol (entidad)",
  REVOKE_MEMBER: "Revocado por la entidad",
  ENTITY_ACCOUNT_CREATED: "Cuenta creada",
};

const AUDIT_FIELD_LABELS = {
  usertitle: "Título",
  name: "Nombres",
  lastName: "Apellidos",
  nuip: "Documento",
  phone: "Teléfono",
  address: "Dirección",
  city: "Ciudad",
  country: "País",
  postal: "Código postal",
  birthDate: "Fecha de nacimiento",
  avatarUrl: "Foto",
  entity: "Entidad",
  dependence: "Dependencia",
  level: "Nivel",
  position: "Cargo",
  type: "Tipo de persona",
  roles: "Roles",
  permissions: "Permisos",
  area: "Área",
  allowedNatures: "Naturalezas permitidas",
  contractTypes: "Tipos de contrato",
};

const roleNames = (roles) => (roles || []).map((r) => ROLE_LABELS[r] || r);

// Beyond this many entries a list change is shown as counts. The stored
// permissions are a snapshot that expands every role, so adding one role
// lists dozens of permissions and buries the change that mattered.
const AUDIT_LIST_LIMIT = 5;

// One changed field of an UPDATE_USER row: "Cargo: Asesor → Profesional",
// "Roles: +Archivador −Usuario". `full` never shortens a list (the tooltip).
const describeChange = (field, change, full) => {
  const label = AUDIT_FIELD_LABELS[field] || field;
  if (field === "avatarUrl") return `${label} actualizada`;
  if (change.added || change.removed) {
    const names = field === "roles" ? roleNames : (list) => list || [];
    const added = names(change.added);
    const removed = names(change.removed);
    if (!full && added.length + removed.length > AUDIT_LIST_LIMIT) {
      const counts = [];
      if (added.length) counts.push(`+${added.length}`);
      if (removed.length) counts.push(`−${removed.length}`);
      return `${label}: ${counts.join(" ")}`;
    }
    const parts = [...added.map((x) => `+${x}`), ...removed.map((x) => `−${x}`)];
    return `${label}: ${parts.join(" ")}`;
  }
  const show = (v) => (v === null || v === "" ? "—" : String(v));
  return `${label}: ${show(change.from)} → ${show(change.to)}`;
};

// How many changed fields an UPDATE_USER row names before "+N más".
const AUDIT_SUMMARY_PARTS = 2;

const auditSummary = (row, full = false) => {
  const data = row.relevantData || {};
  switch (row.action) {
    case "CHANGE_EMAIL":
    case "RELEASE_EMAIL":
      return `${data.oldEmail || "—"} → ${data.newEmail || "—"}`;
    case "CREATE_USER":
      return roleNames(data.roles).join(", ");
    case "UPDATE_USER": {
      const parts = Object.entries(data.changes || {})
        .map(([field, change]) => describeChange(field, change, full));
      // One save can change many fields; the row shows the first two and the
      // expanded detail (and the tooltip) the rest.
      const shown = full ? parts : parts.slice(0, AUDIT_SUMMARY_PARTS);
      const more = parts.length - shown.length;
      const text = shown.join("; ") + (more > 0 ? ` (+${more} más)` : "");
      return data.self ? `${text} (el propio usuario)` : text;
    }
    case "SET_MEMBER_ROLE":
      return ROLE_LABELS[data.role] || data.role || "";
    default:
      return "";
  }
};

// Everything an UPDATE_USER row changed, for its expandable detail, split in
// list fields (roles, permissions, natures, contract types: added/removed)
// and single values (from/to). One save can touch several of each.
const updateDetailOf = (row) => {
  const empty = { lists: [], values: [] };
  if (row.action !== "UPDATE_USER") return empty;
  const changes = (row.relevantData && row.relevantData.changes) || {};
  return Object.entries(changes).reduce((acc, [field, change]) => {
    const label = AUDIT_FIELD_LABELS[field] || field;
    if (change.added || change.removed) {
      const names = field === "roles" ? roleNames : (list) => list || [];
      acc.lists.push({ key: field, label, added: names(change.added), removed: names(change.removed) });
    } else if (field === "avatarUrl") {
      acc.values.push({ key: field, label, from: "—", to: "Foto nueva" });
    } else {
      const show = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));
      acc.values.push({ key: field, label, from: show(change.from), to: show(change.to) });
    }
    return acc;
  }, empty);
};

// A row is worth expanding when its summary cannot say it all: a list moved
// (dozens of permissions) or more than one field changed.
const isExpandable = ({ lists, values }) => lists.length > 0 || lists.length + values.length > 1;

const AccessChips = ({ items, color }) => (
  items.length === 0 ? (
    <Typography variant="caption" color="text.secondary">—</Typography>
  ) : (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {items.map((item) => (
        <Chip key={item} label={item} size="small" variant="outlined" color={color} />
      ))}
    </Box>
  )
);

const detailTableSx = { my: 1, "& td, & th": { borderBottom: "none" } };

// Everything an UPDATE_USER row changed, shown under the row when expanded:
// lists as added/removed chips, single values as before/after.
function UpdateDetail({ detail }) {
  return (
    <>
      {detail.lists.length > 0 && (
        <Table size="small" sx={detailTableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 150 }} />
              <TableCell sx={{ fontWeight: 600, width: "42%" }}>Agregados</TableCell>
              <TableCell sx={{ fontWeight: 600, width: "42%" }}>Removidos</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {detail.lists.map((c) => (
              <TableRow key={c.key}>
                <TableCell sx={{ fontWeight: 600, verticalAlign: "top" }}>{c.label}</TableCell>
                <TableCell sx={{ verticalAlign: "top" }}><AccessChips items={c.added} color="success" /></TableCell>
                <TableCell sx={{ verticalAlign: "top" }}><AccessChips items={c.removed} color="error" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {detail.values.length > 0 && (
        <Table size="small" sx={detailTableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: 150 }}>Campo</TableCell>
              <TableCell sx={{ fontWeight: 600, width: "42%" }}>Antes</TableCell>
              <TableCell sx={{ fontWeight: 600, width: "42%" }}>Después</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {detail.values.map((c) => (
              <TableRow key={c.key}>
                <TableCell sx={{ fontWeight: 600 }}>{c.label}</TableCell>
                <TableCell sx={{ color: "text.secondary", wordBreak: "break-word" }}>{c.from}</TableCell>
                <TableCell sx={{ wordBreak: "break-word" }}>{c.to}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
}

// Everything done to the selected user: by administrators, by the user
// themselves and, for a company account, by its company's administrators.
// A row that changed a list (roles, permissions...) or several fields at once
// expands into everything it changed.
// Fetched on its own once the user is opened, and again whenever `refreshKey`
// changes (after any edit made here). A reply for a user who is no longer
// selected is dropped, so clicking quickly through users never shows one
// person's history on another.
function UserAuditPanel({ userId, currentUser, refreshKey }) {
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  useEffect(() => {
    let active = true;
    setRows(null);
    setFailed(false);
    setExpanded(new Set());
    getUserAudit(userId, currentUser).then((audit) => {
      if (!active) return;
      if (audit) setRows(audit);
      else setFailed(true);
    });
    return () => { active = false; };
  }, [userId, currentUser, refreshKey]);

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Historial</Typography>
      {failed ? (
        <Typography variant="body2" color="error">No fue posible cargar el historial.</Typography>
      ) : rows === null ? (
        <CircularProgress size={20} />
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">Sin cambios registrados.</Typography>
      ) : (
        <TableContainer sx={{ maxHeight: 360 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Fecha</TableCell>
                <TableCell>Acción</TableCell>
                <TableCell>Detalle</TableCell>
                <TableCell>Realizado por</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const detail = updateDetailOf(row);
                const expandable = isExpandable(detail);
                const open = expandable && expanded.has(row.id);
                return (
                  <React.Fragment key={row.id}>
                    <TableRow
                      hover={expandable}
                      onClick={expandable ? () => toggle(row.id) : undefined}
                      sx={{
                        cursor: expandable ? "pointer" : "default",
                        "& > td": open ? { borderBottom: "none" } : undefined,
                      }}
                    >
                      <TableCell padding="checkbox">
                        {expandable && (
                          <IconButton
                            size="small"
                            aria-label={open ? "Ocultar detalle" : "Ver detalle"}
                            onClick={(e) => { e.stopPropagation(); toggle(row.id); }}
                          >
                            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {row.timestamp ? new Date(row.timestamp).toLocaleString("es-CO") : "—"}
                      </TableCell>
                      <TableCell>{AUDIT_ACTION_LABELS[row.action] || row.action}</TableCell>
                      <TableCell sx={{ wordBreak: "break-word" }} title={auditSummary(row, true)}>
                        {auditSummary(row)}
                      </TableCell>
                      <TableCell>{row.userName || "—"}</TableCell>
                    </TableRow>
                    {expandable && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ py: 0, ...(open ? {} : { borderBottom: "none" }) }}>
                          <Collapse in={open} timeout="auto" unmountOnExit>
                            <UpdateDetail detail={detail} />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// The selected user's profile, editable. The email is shown here but moves
// only through CHANGE_EMAIL (full ADMIN, never on themselves), because it is
// also the Firebase Auth sign-in address.
function UserProfileEditor({ selectedUser, currentUser, onUpdated, onEmailChanged, setSnack }) {
  const [profile, setProfile] = useState(profileOf(selectedUser));
  const [saving, setSaving] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  // The record the form was last loaded from, to tell edits from stale values.
  const loaded = useRef({ userId: selectedUser.userId, profile: profileOf(selectedUser) });

  // Another user: start over. The SAME user refreshed (after saving roles, or
  // an email change): take the fresh values but keep what was typed and not
  // yet saved. Reloading everything used to wipe those edits silently.
  useEffect(() => {
    const fresh = profileOf(selectedUser);
    const prev = loaded.current;
    loaded.current = { userId: selectedUser.userId, profile: fresh };
    if (prev.userId !== selectedUser.userId) {
      setProfile(fresh);
      return;
    }
    setProfile((current) => Object.fromEntries(Object.keys(fresh).map((f) => [
      f, current[f] !== prev.profile[f] ? current[f] : fresh[f],
    ])));
  }, [selectedUser]);

  const isEntity = selectedUser.userType === "ENTITY";
  const isSelf = selectedUser.userId === currentUser.userId;
  const canChangeEmail = !!currentUser.roles?.includes("ADMIN") && !isSelf && !isEntity;
  const fields = PROFILE_FIELDS.filter((f) => !(isEntity && STAFF_ONLY_FIELDS.includes(f)));
  const changed = fields.filter((f) => profile[f] !== profileOf(selectedUser)[f]);

  const set = (field) => (evt) => setProfile((prev) => ({ ...prev, [field]: evt.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = { userId: selectedUser.userId };
      changed.forEach((f) => { body[f] = profile[f]; });
      const response = await updateUser(body, currentUser);
      if (response && response.result) {
        onUpdated({ ...selectedUser, ...body });
        setSnack({ open: true, message: "Perfil actualizado", severity: "success" });
      } else {
        setSnack({ open: true, message: "Error al actualizar el perfil", severity: "error" });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
        <Typography variant="subtitle2">Perfil</Typography>
        <CopyableUserId userId={selectedUser.userId} />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">Correo</Typography>
              <Typography variant="body2" sx={{ wordBreak: "break-all" }}>{selectedUser.email}</Typography>
            </Box>
            {canChangeEmail && (
              <Button size="small" variant="outlined" onClick={() => setEmailDialogOpen(true)}>
                Cambiar correo
              </Button>
            )}
            {isEntity && (
              <Typography variant="caption" color="text.secondary">
                La cuenta de empresa cambia su correo desde su perfil.
              </Typography>
            )}
          </Box>
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Título</InputLabel>
            <Select value={profile.usertitle} label="Título" onChange={set("usertitle")}>
              <MenuItem value="Señor">Señor</MenuItem>
              <MenuItem value="Señora">Señora</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={5}>
          <TextField fullWidth size="small" label="Nombres" value={profile.name} onChange={set("name")} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth size="small" label="Apellidos" value={profile.lastName} onChange={set("lastName")} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Número de identificación (NUIP)" value={profile.nuip} onChange={set("nuip")} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Ciudad" value={profile.city} onChange={set("city")} />
        </Grid>
        {!isEntity && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Entidad" value={profile.entity} onChange={set("entity")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                size="small"
                options={dependencies}
                value={profile.dependence}
                onInputChange={(_, value) => setProfile((prev) => ({ ...prev, dependence: value }))}
                renderInput={(params) => <TextField {...params} label="Dependencia" />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Nivel" value={profile.level} onChange={set("level")} />
            </Grid>
          </>
        )}
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Cargo" value={profile.position} onChange={set("position")} />
        </Grid>
      </Grid>
      <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
        <Button variant="contained" size="small" onClick={handleSave} disabled={saving || changed.length === 0}>
          {saving ? <CircularProgress size={20} /> : "Guardar perfil"}
        </Button>
        {changed.length > 0 && (
          <Typography variant="caption" color="warning.main" sx={{ alignSelf: "center" }}>
            Cambios sin guardar
          </Typography>
        )}
        {changed.length > 0 && (
          <Button variant="text" size="small" onClick={() => setProfile(profileOf(selectedUser))}>
            Descartar
          </Button>
        )}
      </Box>

      <ChangeEmailDialog
        open={emailDialogOpen}
        targetUser={selectedUser}
        currentUser={currentUser}
        onClose={() => setEmailDialogOpen(false)}
        onChanged={(email) => {
          setEmailDialogOpen(false);
          onUpdated({ ...selectedUser, email });
          onEmailChanged();
          setSnack({ open: true, message: "Correo actualizado", severity: "success" });
        }}
      />
    </Box>
  );
}

// A company account's ENTITY_* role is managed by its company (Mi Entidad) or
// from the entity record, never from here. The one staff role a company may
// hold, Contratista Persona Jurídica, is granted here: handleUser refuses
// anything else on these accounts (ENTITY_ACCOUNT).
const ENTITY_GRANTABLE_ROLE = "CONTRACTOR_LEGAL";

function EntityAccessNotice({ user, canManage, saving, onToggleContractor }) {
  const roles = (user.roles || []).map((r) => ROLE_LABELS[r] || r);
  const isContractor = (user.roles || []).includes(ENTITY_GRANTABLE_ROLE);
  return (
    <Alert
      severity="info"
      sx={{ mt: 1 }}
      action={user.entityNit && (
        <Button color="inherit" size="small" component={RouterLink} to={`/entities/${user.entityNit}`}>
          Ver entidad
        </Button>
      )}
    >
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        Cuenta de empresa{roles.length ? ` · ${roles.join(", ")}` : ""}
      </Typography>
      <Typography variant="body2">
        Su rol en la empresa se gestiona desde la entidad. Desde aquí solo se asigna
        {" "}{ROLE_LABELS[ENTITY_GRANTABLE_ROLE]}.
      </Typography>
      {canManage && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
          <Switch
            size="small"
            checked={isContractor}
            disabled={saving}
            onChange={(e) => onToggleContractor(e.target.checked)}
            inputProps={{ "aria-label": ROLE_LABELS[ENTITY_GRANTABLE_ROLE] }}
          />
          <Typography variant="body2">{ROLE_LABELS[ENTITY_GRANTABLE_ROLE]}</Typography>
          {saving && <CircularProgress size={16} />}
        </Box>
      )}
    </Alert>
  );
}

// Alcance por tipo de contrato (users.contractTypes). Independiente del rol:
// vacío = todos los tipos; lo impone el backend (contract-type-scope).
function ContractTypesSelector({ value, onChange }) {
  return (
    <Autocomplete
      multiple
      options={CONTRACT_TYPES}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Tipos de contrato"
          helperText="Limita los contratos que el usuario ve y gestiona al prefijo del código (p. ej. DEX). Vacío = todos los tipos."
        />
      )}
    />
  );
}

function ExistingUserEditor({ users, currentUser, isFullAdmin = true }) {
  const dispatch = useDispatch();
  const contractCfg = useNatureCatalog();
  const [selectedUser, setSelectedUser] = useState(null);
  const [editRoles, setEditRoles] = useState([]);
  const [editPermissions, setEditPermissions] = useState([]);
  const [editArea, setEditArea] = useState("");
  const [editAllowedNatures, setEditAllowedNatures] = useState([]);
  const [editContractTypes, setEditContractTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [auditRefresh, setAuditRefresh] = useState(0);

  const sortedUsers = useMemo(() => {
    if (!users) return [];
    return [...users].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "")
    );
  }, [users]);

  const handleSelectUser = (_, user) => {
    setSelectedUser(user);
    if (user) {
      setEditRoles(user.roles || []);
      setEditPermissions(user.permissions || []);
      setEditArea(user.area || "");
      setEditAllowedNatures(user.allowedNatures || []);
      setEditContractTypes(user.contractTypes || []);
    } else {
      setEditRoles([]);
      setEditPermissions([]);
      setEditArea("");
      setEditAllowedNatures([]);
      setEditContractTypes([]);
    }
  };

  const handleToggleRole = (role) => {
    if (!isFullAdmin && ADMIN_ONLY_ROLES.includes(role)) return;
    const isRemoving = editRoles.includes(role);
    setEditRoles((prev) =>
      isRemoving ? prev.filter((r) => r !== role) : [...prev, role]
    );
    const rolePerms = ROLES[role] || [];
    if (isRemoving) {
      const remaining = editRoles.filter((r) => r !== role);
      setEditPermissions((prev) => permissionsAfterRoleRemoval(prev, role, remaining));
    } else {
      setEditPermissions((prev) => [...new Set([...prev, ...rolePerms])]);
    }
  };

  const handleTogglePermission = (permission) => {
    setEditPermissions((prev) =>
      prev.includes(permission) ? prev.filter((p) => p !== permission) : [...prev, permission]
    );
  };

  const handleSaveRoles = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      const body = {
        userId: selectedUser.userId,
        roles: editRoles,
        permissions: editPermissions,
        ...(editRoles.includes("SUPERVISOR_CONTRACT") && { area: editArea }),
        ...(editRoles.includes("FINANCIER") && { allowedNatures: editAllowedNatures }),
        contractTypes: editContractTypes,
      };
      const response = await updateUser(body, currentUser);
      if (response && response.result) {
        const updatedUser = {
          ...selectedUser, roles: editRoles, permissions: editPermissions, contractTypes: editContractTypes,
        };
        if (editRoles.includes("SUPERVISOR_CONTRACT")) updatedUser.area = editArea;
        if (editRoles.includes("FINANCIER")) updatedUser.allowedNatures = editAllowedNatures;
        dispatch(updateUsers(updatedUser));
        setSelectedUser(updatedUser);
        setAuditRefresh((n) => n + 1);
        setSnack({ open: true, message: "Roles y permisos actualizados exitosamente", severity: "success" });
      } else {
        setSnack({ open: true, message: "Error al actualizar roles", severity: "error" });
      }
    } catch (error) {
      debugLog("Error updating roles: ", error);
      setSnack({ open: true, message: "Error al actualizar roles", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  // Grants or removes the one staff role a company account may hold, with
  // its permissions (the backend refuses one without the other).
  const handleToggleEntityContractor = async (grant) => {
    if (!selectedUser) return;
    const roles = selectedUser.roles || [];
    const nextRoles = grant
      ? [...new Set([...roles, ENTITY_GRANTABLE_ROLE])]
      : roles.filter((r) => r !== ENTITY_GRANTABLE_ROLE);
    const nextPermissions = grant
      ? [...new Set([...(selectedUser.permissions || []), ...(ROLES[ENTITY_GRANTABLE_ROLE] || [])])]
      : permissionsAfterRoleRemoval(selectedUser.permissions || [], ENTITY_GRANTABLE_ROLE, nextRoles);
    setSaving(true);
    try {
      const body = { userId: selectedUser.userId, roles: nextRoles, permissions: nextPermissions };
      const response = await updateUser(body, currentUser);
      if (response && response.result) {
        const updatedUser = { ...selectedUser, roles: nextRoles, permissions: nextPermissions };
        dispatch(updateUsers(updatedUser));
        setSelectedUser(updatedUser);
        setEditRoles(nextRoles);
        setEditPermissions(nextPermissions);
        setAuditRefresh((n) => n + 1);
        setSnack({
          open: true,
          message: grant ? "Rol de contratista asignado" : "Rol de contratista retirado",
          severity: "success",
        });
      } else {
        setSnack({ open: true, message: "No se pudo actualizar el rol de contratista", severity: "error" });
      }
    } finally {
      setSaving(false);
    }
  };

  const canManageUsers = !!currentUser.roles?.includes("ADMIN")
    || !!currentUser.permissions?.includes(PERMISSIONS.USER_ADMIN.MANAGE);

  const hasChanges = selectedUser && (
    JSON.stringify([...editRoles].sort()) !== JSON.stringify([...(selectedUser.roles || [])].sort()) ||
    JSON.stringify([...editPermissions].sort()) !== JSON.stringify([...(selectedUser.permissions || [])].sort()) ||
    (editRoles.includes("SUPERVISOR_CONTRACT") && editArea !== (selectedUser.area || "")) ||
    (editRoles.includes("FINANCIER") &&
      JSON.stringify([...editAllowedNatures].sort()) !== JSON.stringify([...(selectedUser.allowedNatures || [])].sort())) ||
    JSON.stringify([...editContractTypes].sort()) !== JSON.stringify([...(selectedUser.contractTypes || [])].sort())
  );

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Usuarios
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Busque un usuario para editar su perfil, correo, roles y permisos
      </Typography>

      <Autocomplete
        options={sortedUsers}
        value={selectedUser}
        onChange={handleSelectUser}
        getOptionLabel={(option) =>
          `${option.name || ""} ${option.lastName || ""} — ${option.email || ""}`
        }
        isOptionEqualToValue={(option, value) => option.userId === value.userId}
        renderInput={(params) => (
          <TextField {...params} label="Buscar usuario" placeholder="Nombre o correo..." />
        )}
        sx={{ mb: 3 }}
      />

      {selectedUser && (
        <>
          <UserProfileEditor
            selectedUser={selectedUser}
            currentUser={currentUser}
            setSnack={setSnack}
            onUpdated={(updatedUser) => {
              dispatch(updateUsers(updatedUser));
              setSelectedUser(updatedUser);
              setAuditRefresh((n) => n + 1);
            }}
            onEmailChanged={() => setAuditRefresh((n) => n + 1)}
          />

          <UserAuditPanel
            userId={selectedUser.userId}
            currentUser={currentUser}
            refreshKey={auditRefresh}
          />

          {selectedUser.userType === "ENTITY" ? (
            <EntityAccessNotice
              user={selectedUser}
              canManage={canManageUsers}
              saving={saving}
              onToggleContractor={handleToggleEntityContractor}
            />
          ) : (
          <>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Roles y permisos</Typography>
          <RolePermissionsTable
            selectedRoles={editRoles}
            onToggleRole={handleToggleRole}
            extraPermissions={editPermissions}
            onTogglePermission={handleTogglePermission}
            isFullAdmin={isFullAdmin}
          />

          {editRoles.includes("SUPERVISOR_CONTRACT") && (
            <Box sx={{ mt: 2 }}>
              <Autocomplete
                freeSolo
                options={dependencies}
                value={editArea}
                onInputChange={(_, newValue) => setEditArea(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Área (requerida para Supervisor)" />
                )}
              />
            </Box>
          )}

          {editRoles.includes("FINANCIER") && (
            <Box sx={{ mt: 2 }}>
              <Autocomplete
                multiple
                options={allNatureOptions(contractCfg)}
                getOptionLabel={natureLabelFor(contractCfg)}
                value={editAllowedNatures}
                onChange={(_, newValue) => setEditAllowedNatures(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Naturalezas de contrato permitidas (Financiero)"
                    helperText="El financiero solo verá contratos de las naturalezas seleccionadas. Sin selección, no verá ningún contrato."
                  />
                )}
              />
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <ContractTypesSelector value={editContractTypes} onChange={setEditContractTypes} />
          </Box>

          <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              onClick={handleSaveRoles}
              disabled={saving || !hasChanges}
            >
              {saving ? <CircularProgress size={24} /> : <span>Guardar Cambios</span>}
            </Button>
            {hasChanges && (
              <Button
                variant="text"
                onClick={() => {
                  setEditRoles(selectedUser.roles || []);
                  setEditArea(selectedUser.area || "");
                  setEditAllowedNatures(selectedUser.allowedNatures || []);
                  setEditContractTypes(selectedUser.contractTypes || []);
                }}
              >
                Descartar
              </Button>
            )}
          </Box>
          </>
          )}
        </>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

// What handleUser's RELEASE_EMAIL leaves as the address: the old one plus a
// suffix no mailbox answers.
const RELEASED_EMAIL = /\.released[0-9a-f]{6}$/;

const RELEASE_ERRORS = {
  USER_NOT_DISABLED: "Solo se puede liberar el correo de un usuario deshabilitado.",
  ALREADY_RELEASED: "El correo de este usuario ya fue liberado.",
  ENTITY_ACCOUNT: "Las cuentas de empresa gestionan su correo desde su propio perfil.",
  NO_AUTH_ACCOUNT: "El usuario no tiene una cuenta de acceso asociada.",
  FORBIDDEN: "Solo un administrador puede liberar correos.",
};

const DISABLE_ERRORS = {
  USER_NOT_FOUND: "El usuario no existe.",
  NO_AUTH_ACCOUNT: "El usuario no tiene una cuenta de acceso asociada.",
  FORBIDDEN: "No tiene permiso para realizar esta acción.",
};

function UserDisableManager({ users, currentUser }) {
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [processing, setProcessing] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, user: null, disable: true });
  const [releaseTarget, setReleaseTarget] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  // Freeing an address is only for a full ADMIN, on a disabled staff account
  // that still has its own address (handleUser enforces all of it).
  const isFullAdmin = !!currentUser.roles?.includes("ADMIN");
  const canRelease = (u) =>
    isFullAdmin && u.disabled && u.userType !== "ENTITY" && !!u.email && !RELEASED_EMAIL.test(u.email);

  const handleRelease = async () => {
    const target = releaseTarget;
    setReleaseTarget(null);
    if (!target) return;
    setProcessing(target.userId);
    try {
      const response = await releaseUserEmail(target.userId, currentUser);
      if (response && response.result) {
        dispatch(updateUsers({ ...target, email: response.email }));
        setSnack({
          open: true,
          message: `Correo ${target.email} liberado. Ya puede asignarse a otro usuario.`,
          severity: "success",
        });
      } else {
        setSnack({
          open: true,
          message: RELEASE_ERRORS[response?.error] || "No se pudo liberar el correo",
          severity: "error",
        });
      }
    } finally {
      setProcessing(null);
    }
  };

  const filteredUsers = useMemo(() => {
    if (!users) return [];
    const sorted = [...users].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    if (!search.trim()) return sorted;
    const term = search.toLowerCase();
    return sorted.filter(
      (u) =>
        (u.name || "").toLowerCase().includes(term) ||
        (u.lastName || "").toLowerCase().includes(term) ||
        (u.email || "").toLowerCase().includes(term) ||
        (u.nuip || "").toLowerCase().includes(term)
    );
  }, [users, search]);

  const openConfirm = (targetUser, disable) => {
    setConfirmDialog({ open: true, user: targetUser, disable });
  };

  const handleConfirm = async () => {
    const { user: targetUser, disable } = confirmDialog;
    setConfirmDialog({ open: false, user: null, disable: true });
    if (!targetUser) return;

    setProcessing(targetUser.userId);
    try {
      const response = await disableUserService(targetUser.userId, disable, currentUser);
      if (response && response.result) {
        dispatch(updateUsers({ ...targetUser, disabled: disable }));
        setSnack({
          open: true,
          message: disable
            ? `Usuario ${targetUser.name} ${targetUser.lastName} deshabilitado`
            : `Usuario ${targetUser.name} ${targetUser.lastName} habilitado`,
          severity: "success",
        });
      } else {
        setSnack({
          open: true,
          message: DISABLE_ERRORS[response?.error] || "No se pudo actualizar el usuario",
          severity: "error",
        });
      }
    } catch (error) {
      debugLog("Error toggling user status: ", error);
      setSnack({ open: true, message: "Error al actualizar el estado del usuario", severity: "error" });
    } finally {
      setProcessing(null);
    }
  };

  const activeUsers = filteredUsers.filter((u) => !u.disabled);
  const disabledUsers = filteredUsers.filter((u) => u.disabled);

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Deshabilitar / Habilitar Usuarios
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Deshabilitar un usuario bloquea su acceso al sistema. Puede habilitarlo nuevamente en cualquier momento.
      </Typography>

      <TextField
        fullWidth
        size="small"
        placeholder="Buscar por nombre, correo o NUIP..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      {activeUsers.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Usuarios Activos ({activeUsers.length})
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.50" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Correo</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Roles</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeUsers.map((u) => {
                  const isSelf = u.userId === currentUser.userId;
                  return (
                    <TableRow key={u.userId} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {u.name} {u.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="div">
                          {u.nuip}
                        </Typography>
                        <CopyableUserId userId={u.userId} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{u.email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {(u.roles || []).map((role) => (
                            <Chip
                              key={role}
                              label={ROLE_LABELS[role] || role}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                          {(!u.roles || u.roles.length === 0) && (
                            <Typography variant="caption" color="text.secondary">Sin roles</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {processing === u.userId ? (
                          <CircularProgress size={24} />
                        ) : (
                          <Tooltip title={isSelf ? "No puede deshabilitarse a sí mismo" : "Deshabilitar usuario"}>
                            <span>
                              <Switch
                                checked
                                color="success"
                                disabled={isSelf}
                                onChange={() => openConfirm(u, true)}
                              />
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {disabledUsers.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1, color: "error.main" }}>
            Usuarios Deshabilitados ({disabledUsers.length})
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "error.50" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Correo</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Roles</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {disabledUsers.map((u) => (
                  <TableRow key={u.userId} hover sx={{ opacity: 0.75 }}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <BlockIcon fontSize="small" color="error" />
                        <Box>
                          <Typography variant="body2">
                            {u.name} {u.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="div">
                            {u.nuip}
                          </Typography>
                          <CopyableUserId userId={u.userId} />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>{u.email}</Typography>
                      {canRelease(u) && (
                        <Button
                          size="small"
                          sx={{ mt: 0.5, px: 0 }}
                          disabled={processing === u.userId}
                          onClick={() => setReleaseTarget(u)}
                        >
                          Liberar correo
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {(u.roles || []).map((role) => (
                          <Chip
                            key={role}
                            label={ROLE_LABELS[role] || role}
                            size="small"
                            variant="outlined"
                            color="default"
                          />
                        ))}
                        {(!u.roles || u.roles.length === 0) && (
                          <Typography variant="caption" color="text.secondary">Sin roles</Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {processing === u.userId ? (
                        <CircularProgress size={24} />
                      ) : (
                        <Tooltip title="Habilitar usuario">
                          <Switch
                            checked={false}
                            color="success"
                            onChange={() => openConfirm(u, false)}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {filteredUsers.length === 0 && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            {search ? "No se encontraron usuarios con ese criterio" : "No hay usuarios disponibles"}
          </Typography>
        </Box>
      )}

      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, user: null, disable: true })}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {confirmDialog.disable ? (
            <><BlockIcon color="error" /> Deshabilitar Usuario</>
          ) : (
            <><CheckCircleIcon color="success" /> Habilitar Usuario</>
          )}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.disable
              ? `¿Está seguro que desea deshabilitar a ${confirmDialog.user?.name} ${confirmDialog.user?.lastName}? El usuario no podrá iniciar sesión en el sistema.`
              : `¿Está seguro que desea habilitar a ${confirmDialog.user?.name} ${confirmDialog.user?.lastName}? El usuario podrá volver a iniciar sesión en el sistema.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, user: null, disable: true })}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color={confirmDialog.disable ? "error" : "success"}
            onClick={handleConfirm}
          >
            {confirmDialog.disable ? "Deshabilitar" : "Habilitar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!releaseTarget} onClose={() => setReleaseTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Liberar correo</DialogTitle>
        <DialogContent>
          <DialogContentText>
            El correo <strong>{releaseTarget?.email}</strong> dejará de pertenecer a{" "}
            {releaseTarget?.name} {releaseTarget?.lastName} y podrá asignarse a un usuario nuevo.
            La cuenta sigue deshabilitada y conserva su historial; si se habilita de nuevo,
            necesitará un correo nuevo para iniciar sesión.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReleaseTarget(null)}>Cancelar</Button>
          <Button variant="contained" color="warning" onClick={handleRelease}>
            Liberar correo
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

export default function UserAdmin() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.value);
  const users = useSelector((state) => state.user.users);
  const contractCfg = useNatureCatalog();

  const [activeTab, setActiveTab] = useState("create");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [usertitle, setUsertitle] = useState("Señor");
  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [nuip, setNuip] = useState("");
  const [city, setCity] = useState("");
  const [entity, setEntity] = useState("");
  const [dependence, setDependence] = useState("");
  const [level, setLevel] = useState("");
  const [position, setPosition] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [customPermissions, setCustomPermissions] = useState([]);
  const [area, setArea] = useState("");
  const [allowedNatures, setAllowedNatures] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, message: "", severity: "success" });
  const [createdUser, setCreatedUser] = useState(null);

  const isFullAdmin = user?.roles?.includes("ADMIN");
  const canAdd = user?.permissions?.includes(PERMISSIONS.USER_ADMIN.ADD);
  const canManage = user?.permissions?.includes(PERMISSIONS.USER_ADMIN.MANAGE);
  const canDeactivate = user?.permissions?.includes(PERMISSIONS.USER_ADMIN.DEACTIVATE);
  const hasUserAdminAccess = canAdd || canManage || canDeactivate;

  if (!isFullAdmin && !hasUserAdminAccess) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h6">Acceso denegado</Typography>
      </Container>
    );
  }

  const resetForm = () => {
    setSelectedTemplate("");
    setUsertitle("Señor");
    setName("");
    setLastName("");
    setEmail("");
    setNuip("");
    setCity("");
    setEntity("");
    setDependence("");
    setLevel("");
    setPosition("");
    setSelectedRoles([]);
    setCustomPermissions([]);
    setArea("");
    setAllowedNatures([]);
    setContractTypes([]);
  };

  const handleTemplateApply = (templateKey) => {
    if (!templateKey) {
      setSelectedTemplate("");
      setSelectedRoles([]);
      setCustomPermissions([]);
      return;
    }
    setSelectedTemplate(templateKey);
    const template = USER_TEMPLATES[templateKey];
    setSelectedRoles([...template.roles]);
    const rolePerms = template.roles.flatMap((r) => ROLES[r] || []);
    setCustomPermissions([...new Set([...rolePerms, ...(template.permissions || [])])]);
  };

  const handleToggleRole = (role) => {
    if (!isFullAdmin && ADMIN_ONLY_ROLES.includes(role)) return;
    const isRemoving = selectedRoles.includes(role);
    setSelectedRoles((prev) =>
      isRemoving ? prev.filter((r) => r !== role) : [...prev, role]
    );
    const rolePerms = ROLES[role] || [];
    if (isRemoving) {
      const remaining = selectedRoles.filter((r) => r !== role);
      setCustomPermissions((prev) => permissionsAfterRoleRemoval(prev, role, remaining));
    } else {
      setCustomPermissions((prev) => [...new Set([...prev, ...rolePerms])]);
    }
    setSelectedTemplate("");
  };

  const handleTogglePermission = (permission) => {
    setCustomPermissions((prev) =>
      prev.includes(permission) ? prev.filter((p) => p !== permission) : [...prev, permission]
    );
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    evt.stopPropagation();

    if (nuip.length < 6) {
      setSnackInfo({
        open: true,
        message: "El NUIP debe tener al menos 6 caracteres (se usa como contraseña inicial)",
        severity: "warning",
      });
      return;
    }

    if (selectedRoles.includes("SUPERVISOR_CONTRACT") && !area) {
      setSnackInfo({
        open: true,
        message: "El área es requerida para el rol de Supervisor de Contratos",
        severity: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      const bodyUser = {
        usertitle,
        name,
        lastName,
        email,
        nuip,
        city,
        entity,
        dependence,
        level,
        position,
        roles: selectedRoles,
        permissions: customPermissions,
        password: nuip,
        ...(selectedRoles.includes("SUPERVISOR_CONTRACT") && { area }),
        ...(selectedRoles.includes("FINANCIER") && { allowedNatures }),
        ...(contractTypes.length && { contractTypes }),
      };

      const response = await addUser(bodyUser, user);
      if (response && response.result) {
        bodyUser.userId = response.userId;
        bodyUser.userUID = response.userId;
        dispatch(addUserToStore(bodyUser));
        setCreatedUser(bodyUser);
        setSnackInfo({ open: true, message: "Usuario creado exitosamente", severity: "success" });
        resetForm();
      } else {
        const errorMsg = getErrorMessage(response?.error);
        setSnackInfo({ open: true, message: errorMsg, severity: "error" });
      }
    } catch (error) {
      debugLog("Error creating user: ", error);
      setSnackInfo({ open: true, message: "Error al crear el usuario", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Administración de Usuarios
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Crear usuarios y editar sus perfiles y roles
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {(isFullAdmin || canAdd) && (
            <Button
              variant={activeTab === "create" ? "contained" : "outlined"}
              size="small"
              onClick={() => setActiveTab("create")}
            >
              Crear Usuario
            </Button>
          )}
          {(isFullAdmin || canManage) && (
            <Button
              variant={activeTab === "manage" ? "contained" : "outlined"}
              size="small"
              onClick={() => setActiveTab("manage")}
            >
              Usuarios
            </Button>
          )}
          {(isFullAdmin || canDeactivate) && (
            <Button
              variant={activeTab === "disable" ? "contained" : "outlined"}
              size="small"
              color={activeTab === "disable" ? "error" : "inherit"}
              onClick={() => setActiveTab("disable")}
            >
              Deshabilitar Usuarios
            </Button>
          )}
        </Box>
      </Paper>

      {activeTab === "create" && (isFullAdmin || canAdd) && (
        <>
          <CreatedUserCard createdUser={createdUser} onDismiss={() => setCreatedUser(null)} />

          <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Plantilla rápida (opcional)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
              Seleccione una plantilla para precargar roles, o configure roles manualmente abajo
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Plantilla de usuario</InputLabel>
              <Select
                value={selectedTemplate}
                label="Plantilla de usuario"
                onChange={(evt) => handleTemplateApply(evt.target.value)}
              >
                <MenuItem value="">
                  <em>Sin plantilla — configurar manualmente</em>
                </MenuItem>
                {Object.entries(USER_TEMPLATES).map(([key, tmpl]) => (
                  <MenuItem key={key} value={key}>
                    {tmpl.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>

          <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel>Título</InputLabel>
                    <Select
                      value={usertitle}
                      label="Título"
                      onChange={(evt) => setUsertitle(evt.target.value)}
                    >
                      <MenuItem value="Señor">Señor</MenuItem>
                      <MenuItem value="Señora">Señora</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={5}>
                  <FormControl fullWidth>
                    <TextField
                      label="Nombres"
                      required
                      value={name}
                      onChange={(evt) => setName(evt.target.value)}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <TextField
                      label="Apellidos"
                      required
                      value={lastName}
                      onChange={(evt) => setLastName(evt.target.value)}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <TextField
                      label="Correo electrónico"
                      type="email"
                      required
                      value={email}
                      onChange={(evt) => setEmail(evt.target.value)}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <TextField
                      label="Número de identificación (NUIP)"
                      required
                      value={nuip}
                      onChange={(evt) => setNuip(evt.target.value)}
                      helperText="Se usará como contraseña inicial"
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <TextField
                      label="Ciudad"
                      required
                      value={city}
                      onChange={(evt) => setCity(evt.target.value)}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <TextField
                      label="Entidad"
                      required
                      value={entity}
                      onChange={(evt) => setEntity(evt.target.value)}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    freeSolo
                    options={dependencies}
                    value={dependence}
                    onInputChange={(_, newValue) => setDependence(newValue)}
                    renderInput={(params) => (
                      <TextField {...params} label="Dependencia" required />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <TextField
                      label="Nivel"
                      value={level}
                      onChange={(evt) => setLevel(evt.target.value)}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <TextField
                      label="Cargo"
                      required
                      value={position}
                      onChange={(evt) => setPosition(evt.target.value)}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>

                <Grid item xs={12}>
                  <RolePermissionsTable
                    selectedRoles={selectedRoles}
                    onToggleRole={handleToggleRole}
                    extraPermissions={customPermissions}
                    onTogglePermission={handleTogglePermission}
                    isFullAdmin={isFullAdmin}
                  />
                </Grid>

                {selectedRoles.includes("SUPERVISOR_CONTRACT") && (
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      freeSolo
                      options={dependencies}
                      value={area}
                      onInputChange={(_, newValue) => setArea(newValue)}
                      renderInput={(params) => (
                        <TextField {...params} label="Área (Supervisor)" required />
                      )}
                    />
                  </Grid>
                )}

                {selectedRoles.includes("FINANCIER") && (
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={allNatureOptions(contractCfg)}
                      getOptionLabel={natureLabelFor(contractCfg)}
                      value={allowedNatures}
                      onChange={(_, newValue) => setAllowedNatures(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Naturalezas de contrato permitidas (Financiero)"
                          helperText="El financiero solo verá contratos de las naturalezas seleccionadas. Sin selección, no verá ningún contrato."
                        />
                      )}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <ContractTypesSelector value={contractTypes} onChange={setContractTypes} />
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {selectedRoles.length === 0
                      ? "El usuario será creado sin roles. Puede asignar roles después desde la pestaña \"Usuarios\"."
                      : "La contraseña por defecto será el número de identificación (NUIP)."}
                  </Alert>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={loading}
                    sx={{ mt: 1 }}
                  >
                    {loading ? <CircularProgress size={24} /> : <span>Crear Usuario</span>}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </>
      )}

      {activeTab === "manage" && (isFullAdmin || canManage) && (
        <ExistingUserEditor users={users} currentUser={user} isFullAdmin={isFullAdmin} />
      )}

      {activeTab === "disable" && (isFullAdmin || canDeactivate) && (
        <UserDisableManager users={users} currentUser={user} />
      )}

      <Snackbar
        open={snackInfo.open}
        autoHideDuration={4000}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackInfo({ ...snackInfo, open: false })}
          severity={snackInfo.severity}
          sx={{ width: "100%" }}
        >
          {snackInfo.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
